/**
 * Audio Recording Service
 * 
 * Handles all audio recording functionality using the MediaRecorder API
 * Provides methods for recording, pausing, resuming, and processing audio
 */
class AudioRecordingService {
  constructor() {
    this.mediaRecorder = null
    this.audioStream = null
    this.audioChunks = []
    this.isRecording = false
    this.isPaused = false
    this.recordingStartTime = null
    this.pausedDuration = 0
    this.onDataAvailable = null
    this.onStop = null
    this.onError = null
    this.onLevelChange = null
  }

  /**
   * Request microphone permission and initialize audio stream
   */
  async requestMicrophonePermission() {
    try {
      this.audioStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100
        }
      })
      
      console.log('🎤 Microphone permission granted')
      return true
    } catch (error) {
      console.error('❌ Microphone permission denied:', error)
      this.onError?.(error)
      return false
    }
  }

  /**
   * Start recording audio
   */
  async startRecording() {
    if (!this.audioStream) {
      const hasPermission = await this.requestMicrophonePermission()
      if (!hasPermission) {
        throw new Error('Microphone permission required')
      }
    }

    if (this.isRecording) {
      console.warn('⚠️ Recording already in progress')
      return
    }

    try {
      // Reset state
      this.audioChunks = []
      this.isRecording = true
      this.isPaused = false
      this.recordingStartTime = Date.now()
      this.pausedDuration = 0

      // Create MediaRecorder with optimal settings
      const options = {
        mimeType: this.getSupportedMimeType(),
        audioBitsPerSecond: 128000 // 128 kbps for good quality/size balance
      }

      this.mediaRecorder = new MediaRecorder(this.audioStream, options)

      // Set up event handlers
      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data)
          this.onDataAvailable?.(event.data)
        }
      }

      this.mediaRecorder.onstop = () => {
        this.isRecording = false
        this.onStop?.(this.getAudioBlob())
      }

      this.mediaRecorder.onerror = (event) => {
        console.error('❌ MediaRecorder error:', event.error)
        this.onError?.(event.error)
      }

      // Start recording
      this.mediaRecorder.start(100) // Collect data every 100ms
      
      // Start audio level monitoring
      this.startAudioLevelMonitoring()

      console.log('🎤 Recording started')
    } catch (error) {
      console.error('❌ Failed to start recording:', error)
      this.onError?.(error)
      throw error
    }
  }

  /**
   * Pause recording
   */
  pauseRecording() {
    if (!this.isRecording || this.isPaused) {
      return
    }

    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.pause()
      this.isPaused = true
      this.pausedDuration += Date.now() - this.recordingStartTime
      console.log('⏸️ Recording paused')
    }
  }

  /**
   * Resume recording
   */
  resumeRecording() {
    if (!this.isRecording || !this.isPaused) {
      return
    }

    if (this.mediaRecorder && this.mediaRecorder.state === 'paused') {
      this.mediaRecorder.resume()
      this.isPaused = false
      this.recordingStartTime = Date.now()
      console.log('▶️ Recording resumed')
    }
  }

  /**
   * Stop recording
   */
  stopRecording() {
    if (!this.isRecording) {
      return
    }

    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop()
    }

    this.stopAudioLevelMonitoring()
    console.log('⏹️ Recording stopped')
  }

  /**
   * Get current recording duration in seconds
   */
  getRecordingDuration() {
    if (!this.isRecording) {
      return 0
    }

    const currentTime = Date.now()
    const activeDuration = this.isPaused ? 0 : (currentTime - this.recordingStartTime)
    const totalDuration = this.pausedDuration + activeDuration
    
    return Math.floor(totalDuration / 1000)
  }

  /**
   * Create audio blob from recorded chunks
   */
  getAudioBlob() {
    if (this.audioChunks.length === 0) {
      return null
    }

    const mimeType = this.getSupportedMimeType()
    const audioBlob = new Blob(this.audioChunks, { type: mimeType })
    
    console.log(`🎵 Audio blob created: ${audioBlob.size} bytes, type: ${mimeType}`)
    return audioBlob
  }

  /**
   * Get supported MIME type for recording
   */
  getSupportedMimeType() {
    const types = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',
      'audio/wav',
      'audio/ogg;codecs=opus'
    ]

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type
      }
    }

    // Fallback
    return 'audio/webm'
  }

  /**
   * Start monitoring audio levels for visualization
   */
  startAudioLevelMonitoring() {
    if (!this.audioStream) return

    // Store references for cleanup
    this.audioContext = new (window.AudioContext || window.webkitAudioContext)()
    this.analyser = this.audioContext.createAnalyser()
    this.microphone = this.audioContext.createMediaStreamSource(this.audioStream)
    
    // Enhanced audio analysis settings
    this.analyser.fftSize = 512
    this.analyser.smoothingTimeConstant = 0.8
    this.microphone.connect(this.analyser)

    this.dataArray = new Uint8Array(this.analyser.frequencyBinCount)
    this.isMonitoringLevels = true

    const monitorLevel = () => {
      if (!this.isMonitoringLevels || !this.audioContext) {
        return
      }

      this.analyser.getByteFrequencyData(this.dataArray)
      
      // Calculate multiple level metrics for better visualization
      let sum = 0
      let peak = 0
      let rms = 0
      
      for (let i = 0; i < this.dataArray.length; i++) {
        const value = this.dataArray[i]
        sum += value
        peak = Math.max(peak, value)
        rms += value * value
      }
      
      const averageLevel = sum / this.dataArray.length
      rms = Math.sqrt(rms / this.dataArray.length)
      
      // Send enhanced level data
      this.onLevelChange?.({
        average: averageLevel,
        peak: peak,
        rms: rms,
        raw: this.dataArray
      })
      
      if (this.isMonitoringLevels) {
        requestAnimationFrame(monitorLevel)
      }
    }

    monitorLevel()
  }

  /**
   * Stop audio level monitoring
   */
  stopAudioLevelMonitoring() {
    this.isMonitoringLevels = false
    
    if (this.audioContext) {
      this.audioContext.close()
      this.audioContext = null
    }
    
    this.analyser = null
    this.microphone = null
    this.dataArray = null
  }

  /**
   * Format duration in seconds to MM:SS format
   */
  formatDuration(seconds) {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  /**
   * Clean up resources
   */
  cleanup() {
    // Stop recording if active
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop()
    }

    // Stop audio level monitoring
    this.stopAudioLevelMonitoring()

    // Stop audio stream
    if (this.audioStream) {
      this.audioStream.getTracks().forEach(track => track.stop())
      this.audioStream = null
    }

    // Reset all state
    this.mediaRecorder = null
    this.audioChunks = []
    this.isRecording = false
    this.isPaused = false
    this.recordingStartTime = null
    this.pausedDuration = 0
    this.isMonitoringLevels = false

    console.log('🧹 Audio recording service cleaned up')
  }

  /**
   * Set event callbacks
   */
  setCallbacks({ onDataAvailable, onStop, onError, onLevelChange }) {
    this.onDataAvailable = onDataAvailable
    this.onStop = onStop
    this.onError = onError
    this.onLevelChange = onLevelChange
  }
}

export default AudioRecordingService
